import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getProductSearchSettings,
  updateProductSearchSettings,
} from '../api/settingsApi';
import { tokenExists } from '../api/auth';
import {
  PRODUCT_SEARCH_MODE_SUGGESTIVE,
  normalizeProductSearchMode,
} from '../utils/productSearch';

export const DEFAULT_PRODUCT_SEARCH_SETTINGS = {
  mode: PRODUCT_SEARCH_MODE_SUGGESTIVE,
};

const ProductSearchSettingsContext = createContext({
  settings: DEFAULT_PRODUCT_SEARCH_SETTINGS,
  searchMode: DEFAULT_PRODUCT_SEARCH_SETTINGS.mode,
  loading: false,
  error: '',
  refreshProductSearchSettings: async () => DEFAULT_PRODUCT_SEARCH_SETTINGS,
  saveProductSearchSettings: async () => DEFAULT_PRODUCT_SEARCH_SETTINGS,
});

const normalizeProductSearchSettings = (value) => ({
  mode: normalizeProductSearchMode(value?.mode),
});

export const ProductSearchSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_PRODUCT_SEARCH_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refreshProductSearchSettings = useCallback(async () => {
    if (!tokenExists()) {
      setSettings(DEFAULT_PRODUCT_SEARCH_SETTINGS);
      setError('');
      return DEFAULT_PRODUCT_SEARCH_SETTINGS;
    }
    setLoading(true);
    setError('');
    try {
      const data = await getProductSearchSettings();
      const normalized = normalizeProductSearchSettings(data);
      setSettings(normalized);
      return normalized;
    } catch (requestError) {
      setSettings(DEFAULT_PRODUCT_SEARCH_SETTINGS);
      setError(requestError?.response?.data?.message || 'No se pudo cargar la configuracion de busqueda.');
      return DEFAULT_PRODUCT_SEARCH_SETTINGS;
    } finally {
      setLoading(false);
    }
  }, []);

  const saveProductSearchSettings = useCallback(async (nextSettings) => {
    setLoading(true);
    setError('');
    try {
      const saved = await updateProductSearchSettings(normalizeProductSearchSettings(nextSettings));
      const normalized = normalizeProductSearchSettings(saved);
      setSettings(normalized);
      return normalized;
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'No se pudo guardar la configuracion de busqueda.');
      throw requestError;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshProductSearchSettings();
  }, [refreshProductSearchSettings]);

  const contextValue = useMemo(() => ({
    settings,
    searchMode: settings.mode,
    loading,
    error,
    refreshProductSearchSettings,
    saveProductSearchSettings,
  }), [
    settings,
    loading,
    error,
    refreshProductSearchSettings,
    saveProductSearchSettings,
  ]);

  return (
    <ProductSearchSettingsContext.Provider value={contextValue}>
      {children}
    </ProductSearchSettingsContext.Provider>
  );
};

const useProductSearchSettings = () => useContext(ProductSearchSettingsContext);

export default useProductSearchSettings;
